import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Users,
  Shield,
  Settings,
  FileText,
  Bot,
  Building2,
  Package,
  DollarSign,
  Users2,
  Megaphone,
  Briefcase,
  Scale,
  Calculator,
  LogOut,
  MapPin,
  PlusCircle,
  Files,
  Copy,
  CheckSquare,
  FolderOpen,
  UserCheck,
  ClipboardList,
  Gift,
  FileSpreadsheet,
  Calendar,
  Receipt,
  Clock,
  ScrollText,
  Tags,
  ArrowUpDown,
  Truck,
  ShoppingBag,
  Archive,
  Building,
  Crown,
  BarChart3,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

const menuItems = [
  {
    title: "Relatórios",
    icon: FileSpreadsheet,
    module: "relatorios",
    children: [
      { title: "Fluxo de contratações", path: "/intranet/relatorios/fluxo-contratacoes", submodule: "fluxo_contratacoes", icon: ArrowUpDown },
      { title: "Log de evolução", path: "/intranet/relatorios/log-evolucao", submodule: "log_evolucao", icon: Clock },
    ],
  },
  {
    title: "Configurações",
    icon: Settings,
    module: "configuracoes",
    children: [
      { title: "Planos", path: "/intranet/configuracoes/planos", submodule: "planos", icon: Crown },
    ],
  },
  {
    title: "Recursos humanos",
    icon: Users,
    module: "recursos_humanos",
    children: [
      { title: "Contratação", path: "/intranet/contratacao", submodule: "contratacao", icon: UserCheck },
      { title: "Cargos", path: "/intranet/recursos-humanos/cargos", submodule: "cargos", icon: Briefcase },
      { title: "Tipo de Contrato", path: "/intranet/recursos-humanos/tipo-contrato", submodule: "tipo_contrato", icon: ScrollText },
      { title: "Modelos de Contrato", path: "/intranet/recursos-humanos/modelos-contrato", submodule: "modelos_contrato", icon: FileText },
      { title: "Tipo de Proposta", path: "/intranet/tipo-proposta", submodule: "tipo_proposta", icon: Settings },
      { title: "Modelos de Proposta", path: "/intranet/modelos-proposta", submodule: "modelos_proposta", icon: FileText },
    ],
  },
  {
    title: "Administração",
    icon: Settings,
    module: "administracao",
    children: [
      { title: "Organizações", path: "/intranet/organizacoes", submodule: "organizacoes", icon: Building },
    ],
  },
];

export function IntranetSidebar() {
  const location = useLocation();
  const { signOut, user } = useAuth();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { hasOrgRole } = useOrgPermissions();
  const [expandedGroups, setExpandedGroups] = useState<string[]>(
    menuItems.map(item => item.module) // Start with all groups expanded
  );

  const toggleGroup = (module: string) => {
    setExpandedGroups(prev =>
      prev.includes(module)
        ? prev.filter(m => m !== module)
        : [...prev, module]
    );
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <Sidebar className="w-64 border-r">
      <SidebarHeader className="p-4">
        <div className="flex items-center space-x-2">
          <div>
            <h2 className="text-lg font-bold">Lyra RH</h2>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2">
        {menuItems.map((group) => {
          const isExpanded = expandedGroups.includes(group.module);
          const hasAnyAccess = group.children?.some(child => 
            hasPermission(group.module, child.submodule)
          );

          if (!hasAnyAccess) return null;

          return (
            <SidebarGroup key={group.module} className="mb-2">
              <SidebarGroupLabel>
                <Button
                  variant="ghost"
                  className="w-full justify-between p-2 h-auto font-medium"
                  onClick={() => toggleGroup(group.module)}
                >
                  <div className="flex items-center space-x-2">
                    <group.icon className="h-4 w-4" />
                    <span>{group.title}</span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </SidebarGroupLabel>

              {isExpanded && (
                <SidebarGroupContent>
                  <SidebarMenu>
                     {group.children?.map((item) => {
                      // Special check for Organizations - only global admins, super admins or org admins/owners can access
                      if (item.submodule === 'organizacoes' && !hasPermission('administracao', 'organizacoes')) {
                        return null;
                      }
                      
                      if (!hasPermission(group.module, item.submodule)) {
                        return null;
                      }

                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton asChild>
                            <NavLink
                              to={item.path}
                              className={({ isActive }) =>
                                `flex items-center space-x-2 px-3 py-2 rounded-md text-sm transition-colors ${
                                  isActive
                                    ? "bg-primary text-primary-foreground"
                                    : "hover:bg-muted"
                                }`
                              }
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              )}
            </SidebarGroup>
          );
        })}

        {/* Super Admin Panel - Only visible to super admins */}
        {isSuperAdmin() && (
          <SidebarGroup className="mb-2">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to="/intranet/super-admin"
                      className={({ isActive }) =>
                        `flex items-center space-x-2 px-3 py-2 rounded-md text-sm transition-colors ${
                          isActive
                            ? "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white"
                            : "bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 hover:from-yellow-100 hover:to-yellow-200"
                        }`
                      }
                    >
                      <Crown className="h-4 w-4 text-yellow-600" />
                      <span className="font-medium text-yellow-700">Super Admin Panel</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="p-4 border-t">
        <div className="space-y-2">
          <div className="text-sm">
            <p className="font-medium">{user?.email}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={signOut}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sair
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}